import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Autocomplete,
} from '@mui/material';
import {
  Public as PublicIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Restore as RestoreIcon,
  Edit as EditIcon,
} from '@mui/icons-material';
import { MapContainer, TileLayer, Polygon } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import ZoneDrawControl, { LatLngPoint } from '../zones/ZoneDrawControl';

interface RegionItem {
  id: string;
  name: string;
  regionType: string;
  parentRegionId?: string;
  geometry: GeoJSON.Geometry;
  archivedDate?: string;
}

// Coarse default view — inland Queensland, roughly where this app's cotton
// growing region data lives (Darling Downs / Central Queensland).
const DEFAULT_CENTER: [number, number] = [-26.5, 148.5];
const DEFAULT_ZOOM = 6;

const ringToLatLngs = (ring: [number, number][]): [number, number][] => ring.map(([lng, lat]) => [lat, lng]);

const geometryToPolygonPositions = (geometry: GeoJSON.Geometry): [number, number][][] | null => {
  if (geometry.type === 'Polygon') {
    return geometry.coordinates.map((ring) => ringToLatLngs(ring as [number, number][]));
  }
  if (geometry.type === 'MultiPolygon') {
    return geometry.coordinates.flatMap((poly) => poly.map((ring) => ringToLatLngs(ring as [number, number][])));
  }
  return null;
};

const pointsToGeoJsonPolygon = (points: LatLngPoint[]): GeoJSON.Geometry => {
  const ring = points.map((p) => [p.lng, p.lat]);
  if (ring.length > 0 && (ring[0][0] !== ring[ring.length - 1][0] || ring[0][1] !== ring[ring.length - 1][1])) {
    ring.push(ring[0]);
  }
  return { type: 'Polygon', coordinates: [ring] };
};

const RegionManagement: React.FC = () => {
  const auth = useAuth();
  const [regions, setRegions] = useState<RegionItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [includeArchived, setIncludeArchived] = useState(false);

  const [drawDialogOpen, setDrawDialogOpen] = useState(false);
  const [drawnGeometry, setDrawnGeometry] = useState<GeoJSON.Geometry | null>(null);
  const [name, setName] = useState('');
  const [regionType, setRegionType] = useState('Cotton Growing Region');
  const [parentRegionId, setParentRegionId] = useState<string | null>(null);

  const [renameTarget, setRenameTarget] = useState<RegionItem | null>(null);
  const [renameName, setRenameName] = useState('');
  const [renameType, setRenameType] = useState('');
  const [renameParentId, setRenameParentId] = useState<string | null>(null);

  const authHeaders = useCallback(() => ({ Authorization: `Bearer ${auth.tokens?.accessToken}` }), [auth.tokens?.accessToken]);

  const fetchRegions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`/api/regions?includeArchived=${includeArchived}`, { headers: authHeaders() });
      if (!response.ok) throw new Error('Failed to fetch regions');
      setRegions(await response.json());
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, [includeArchived, authHeaders]);

  useEffect(() => {
    if (auth.isSignedIn && auth.tokens?.accessToken) {
      fetchRegions();
    }
  }, [fetchRegions, auth.isSignedIn]);

  const handleOpenDraw = () => {
    setDrawnGeometry(null);
    setName('');
    setRegionType('Cotton Growing Region');
    setParentRegionId(null);
    setDrawDialogOpen(true);
  };

  const handleSaveDrawn = async () => {
    if (!drawnGeometry || !name.trim() || !regionType.trim()) return;
    try {
      const response = await fetch('/api/regions', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ name: name.trim(), regionType: regionType.trim(), geometry: drawnGeometry, parentRegionId }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to create region');
      }
      setDrawDialogOpen(false);
      await fetchRegions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleArchive = async (id: string) => {
    if (!window.confirm('Archive this region?')) return;
    try {
      const response = await fetch(`/api/regions/${id}`, { method: 'DELETE', headers: authHeaders() });
      if (!response.ok) throw new Error('Failed to archive region');
      await fetchRegions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleRestore = async (id: string) => {
    try {
      const response = await fetch(`/api/regions/${id}/restore`, { method: 'POST', headers: authHeaders() });
      if (!response.ok) throw new Error('Failed to restore region');
      await fetchRegions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleOpenRename = (region: RegionItem) => {
    setRenameTarget(region);
    setRenameName(region.name);
    setRenameType(region.regionType);
    setRenameParentId(region.parentRegionId ?? null);
  };

  const handleSaveRename = async () => {
    if (!renameTarget || !renameName.trim() || !renameType.trim()) return;
    try {
      const response = await fetch(`/api/regions/${renameTarget.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ name: renameName.trim(), regionType: renameType.trim(), geometry: renameTarget.geometry, parentRegionId: renameParentId }),
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to update region');
      }
      setRenameTarget(null);
      await fetchRegions();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  return (
    <Box>
      <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'stretch', sm: 'center' }} gap={1.5} mb={2}>
        <Typography variant="h6" display="flex" alignItems="center" gap={1}>
          <PublicIcon />
          Cotton Growing Regions
        </Typography>
        <IconButton color="primary" onClick={handleOpenDraw} title="Draw New Region">
          <AddIcon />
        </IconButton>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Hand-drawn regional boundaries used as a spatial fallback when matching imported report data to a
        farm/field isn't possible — the ABS SA4/LGA boundaries seeded elsewhere don't represent real cotton
        growing regions.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading && regions.length === 0 ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : regions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography variant="body2" color="text.secondary">No regions drawn yet.</Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Group</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {regions.map((region) => (
                <TableRow key={region.id} hover>
                  <TableCell>{region.name}</TableCell>
                  <TableCell>{region.regionType}</TableCell>
                  <TableCell>
                    {region.parentRegionId ? regions.find((r) => r.id === region.parentRegionId)?.name || '-' : '-'}
                  </TableCell>
                  <TableCell>
                    {region.archivedDate ? (
                      <Chip label="Archived" size="small" color="default" />
                    ) : (
                      <Chip label="Active" size="small" color="success" />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {region.archivedDate ? (
                      <Tooltip title="Restore">
                        <IconButton size="small" onClick={() => handleRestore(region.id)}>
                          <RestoreIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    ) : (
                      <>
                        <Tooltip title="Rename">
                          <IconButton size="small" onClick={() => handleOpenRename(region)}>
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Archive">
                          <IconButton size="small" color="error" onClick={() => handleArchive(region.id)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Draw New Region dialog */}
      <Dialog open={drawDialogOpen} onClose={() => setDrawDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Draw New Region</DialogTitle>
        <DialogContent>
          <Box display="flex" gap={2} mb={2} mt={1}>
            <TextField label="Name" value={name} onChange={(e) => setName(e.target.value)} fullWidth required />
            <TextField label="Region Type" value={regionType} onChange={(e) => setRegionType(e.target.value)} fullWidth required />
          </Box>
          <Autocomplete
            sx={{ mb: 2 }}
            options={regions.filter((r) => !r.archivedDate)}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            value={regions.find((r) => r.id === parentRegionId) || null}
            onChange={(_, v) => setParentRegionId(v?.id ?? null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Group under (optional)"
                helperText="E.g. group Upper Namoi and Lower Namoi under a parent Namoi region for reporting"
              />
            )}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            Use the polygon tool (top-left of the map) to draw the boundary, then click the last point again to finish.
          </Typography>
          <Box sx={{ height: 450, border: '1px solid', borderColor: 'divider' }}>
            <MapContainer center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM} style={{ height: '100%', width: '100%' }}>
              <TileLayer
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              />
              <ZoneDrawControl
                active
                shapeType="Polygon"
                onDrawn={(_type, points) => setDrawnGeometry(pointsToGeoJsonPolygon(points))}
              />
            </MapContainer>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDrawDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={handleSaveDrawn}
            variant="contained"
            disabled={!drawnGeometry || !name.trim() || !regionType.trim()}
          >
            Save Region
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rename dialog (name/type only — geometry unchanged) */}
      <Dialog open={!!renameTarget} onClose={() => setRenameTarget(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Rename Region</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Name" value={renameName} onChange={(e) => setRenameName(e.target.value)} fullWidth required />
            <TextField label="Region Type" value={renameType} onChange={(e) => setRenameType(e.target.value)} fullWidth required />
            <Autocomplete
              options={regions.filter((r) => !r.archivedDate && r.id !== renameTarget?.id && r.parentRegionId !== renameTarget?.id)}
              getOptionLabel={(o) => o.name}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              value={regions.find((r) => r.id === renameParentId) || null}
              onChange={(_, v) => setRenameParentId(v?.id ?? null)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Group under (optional)"
                  helperText="E.g. group Upper Namoi and Lower Namoi under a parent Namoi region for reporting"
                />
              )}
            />
            {renameTarget && (
              <Box sx={{ height: 250, border: '1px solid', borderColor: 'divider' }}>
                <MapContainer
                  center={DEFAULT_CENTER}
                  zoom={DEFAULT_ZOOM}
                  style={{ height: '100%', width: '100%' }}
                  scrollWheelZoom={false}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  {geometryToPolygonPositions(renameTarget.geometry)?.map((positions, i) => (
                    <Polygon key={i} positions={positions} />
                  ))}
                </MapContainer>
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameTarget(null)}>Cancel</Button>
          <Button onClick={handleSaveRename} variant="contained" disabled={!renameName.trim() || !renameType.trim()}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default RegionManagement;
